import { registerClientActionFromGlob, type ClientActionDescriptor } from "./ClientActionRegistry";

// Glob-based discovery for client-action plugins (Enhancement 08).
//
// Consumer modules — foundation OR domain — ship their client-action
// plugins under `frontend/client-actions/*.tsx` inside their own
// source tree. Each file `export default`s a `ClientActionDescriptor`.
// This module runs once at app boot, globs against both trees, and
// registers every default-exported descriptor.
//
// Vite's `import.meta.glob({eager: true})` resolves at build time —
// the glob is a static string. Paths are RELATIVE to this file
// (`src/frontend/src/managers/clientActionsDiscovery.ts`); since
// foundation + domain trees live OUTSIDE frontend2's Vite root, we
// climb three levels up:
//   `../../../` → `<repo>/src/`
//   `../../../ede/foundation/...` → foundation modules
//   `../../../domains/...`        → domain modules
// (Vite glob does NOT honour leading-slash absolute paths in our
// multi-package layout — they'd resolve from frontend2's root, where
// the source files don't live. Aliases like `@foundation/` work in
// regular imports but not in glob-literal strings.)
//
// Boot-time collision detection — two modules registering the SAME
// command from different paths fails LOUD with both paths named
// (`registerClientActionFromGlob` raises). Loud-fail discipline
// matches the backend's `register_model` duplicate-key check.
//
// Files that don't `export default` a usable descriptor are warned
// (console) and skipped — discovery doesn't crash the app on a
// malformed plugin file.

type _Glob = Record<string, { default?: unknown }>;

/** Foundation tree — `src/ede/foundation/<module>/frontend/client-actions/*.tsx` */
const FOUNDATION_MODULES = import.meta.glob<{ default: unknown }>(
    "../../../ede/foundation/*/frontend/client-actions/*.{ts,tsx}",
    { eager: true },
) as _Glob;

/** Domain tree — `src/domains/<domain>/<module>/frontend/client-actions/*.tsx` */
const DOMAIN_MODULES = import.meta.glob<{ default: unknown }>(
    "../../../domains/*/*/frontend/client-actions/*.{ts,tsx}",
    { eager: true },
) as _Glob;

/** Sanity-check a plugin module's default export. Returns the
    descriptor when shape-valid, null otherwise. */
function _validateDescriptor(value: unknown, path: string): ClientActionDescriptor | null {
    if (!value || typeof value !== "object") {
        // eslint-disable-next-line no-console
        console.warn(`[clientActions] ${path}: no default export — skipping.`);
        return null;
    }
    const d = value as Partial<ClientActionDescriptor>;
    if (typeof d.key !== "string" || d.key.length === 0) {
        // eslint-disable-next-line no-console
        console.warn(`[clientActions] ${path}: default export missing required string field 'key' — skipping.`);
        return null;
    }
    // A React component is either a function (FC / class) or an OBJECT
    // produced by `React.lazy(...)` / `React.memo(...)` / `React.forwardRef(...)`
    // — those carry a `$$typeof` Symbol marker. Accept both so lazy-
    // loaded descriptors (the common case for code-splitting heavy
    // bundles like three.js) don't get silently dropped.
    const isReactComponent = typeof d.component === "function"
        || (typeof d.component === "object" && d.component !== null
            && "$$typeof" in (d.component as object));
    if (!isReactComponent) {
        // eslint-disable-next-line no-console
        console.warn(`[clientActions] ${path}: default export missing required 'component' (React component) — skipping.`);
        return null;
    }
    return d as ClientActionDescriptor;
}

/** Walk a glob result map and register every valid descriptor.
    Exposed (not just called inline) so tests can pass a stub glob
    object to exercise the registration + collision path without
    touching the real filesystem. */
export function registerFromGlob(modules: _Glob): { registered: number; skipped: number } {
    let registered = 0;
    let skipped = 0;
    for (const path in modules) {
        const mod = modules[path];
        const descriptor = _validateDescriptor(mod?.default, path);
        if (!descriptor) {
            skipped += 1;
            continue;
        }
        registerClientActionFromGlob(descriptor, path);
        registered += 1;
    }
    return { registered, skipped };
}

/** App-boot entry point. Called from buildRuntime — runs the glob
    discovery exactly once during startup. Safe to call multiple
    times (idempotent for the bundled-descriptor set since they
    register with a `<bundled:*>` source path that collision
    detection treats as soft); concurrent module imports during
    HMR also work because Vite caches the eager glob result. */
export function discoverClientActions(): { foundation: number; domain: number; skipped: number } {
    const f = registerFromGlob(FOUNDATION_MODULES);
    const d = registerFromGlob(DOMAIN_MODULES);
    return {
        foundation: f.registered,
        domain: d.registered,
        skipped: f.skipped + d.skipped,
    };
}
